import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { DropdownPropsBase, Item } from '@commons/Dropdown/Select/types';
import styles from '@commons/Dropdown/dropdown.scss';
import { translationGetter } from '@commons/Dropdown/utils';
import Select, { SelectProps, SelectState } from '@commons/Select';
import { Option } from '@commons/Select/types';
import { isValidOption, normalizeDropdownData, normalizeSelectData } from '@commons/Select/utils';

import { ChildContext } from '@components/PageStructure/types';
import stripHtml from '@utils/stripHtml';

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

interface DropdownProps extends DropdownPropsBase<Item, false> { }

interface DropdownState {
  options: SelectProps<false>['options'];
  value: SelectProps<false>['value'];
  selectedOptionIndex: number;
}

//----------------------------------------------------------------------
// COMPONENT
//----------------------------------------------------------------------

export default class Dropdown extends Component<DropdownProps, DropdownState> {
  context!: ChildContext;

  static contextTypes = {
    translations: PropTypes.object
  };

  static defaultProps = {
    dropdownList: [],
    label: '',
    isDropdownLabelInactive: false,
    selected: null,
    onChange: noop,
    elementId: '',
    title: ''
  };

  //----------------------------------------------------------------------
  // LIFECYCLE
  //----------------------------------------------------------------------

  constructor(props: DropdownProps) {
    super(props);
    this.state = { options: [], value: null, selectedOptionIndex: 0 };
  }

  static getDerivedStateFromProps(props: DropdownProps) {
    const options = props.dropdownList.map((item) => normalizeDropdownData(item));

    let value = null;
    let selectedOptionIndex = 0;

    if (!isEmpty(props.selected)) {
      const idx = options.findIndex((option) => option.value === props.selected.id);
      value = options[idx];
      selectedOptionIndex = idx;
    }

    return { options, value, selectedOptionIndex };
  }

  //----------------------------------------------------------------------
  // EVENTS
  //----------------------------------------------------------------------

  onChange = (option: Option | null) => {
    if (isValidOption(option)) {
      const mappedItem = normalizeSelectData(option);
      this.props.onChange(mappedItem.id);
    } else {
      this.setState({ selectedOptionIndex: 0 });
      this.props.onChange('');
    }
  };

  //----------------------------------------------------------------------
  // RENDER
  //----------------------------------------------------------------------

  render() {
    const { isDropdownLabelInactive, label, elementId, isRTL, title } = this.props;
    const disabled = this.props.dropdownList.length === 0;
    const labelClasses = classNames(styles.dropdownLabel, {
      // @ts-ignore
      [styles.dropdownLabel_isInactive]: isDropdownLabelInactive
    });

    const placeholder = translationGetter(this.context, 'DROPDOWN_PLACEHOLDER');
    const loadingMessage = translationGetter(this.context, 'DROPDOWN_LOADING_OPTIONS');
    const noOptionsMessage = translationGetter(this.context, 'DROPDOWN_NO_OPTIONS');
    const ariaLabelText = label || title;

    return (
      <div>
        {label && (
          <label htmlFor={elementId} className={labelClasses}>
            {label}
          </label>
        )}

        <Select<false>
          aria-label={ariaLabelText && stripHtml(ariaLabelText)}
          id={elementId}
          isDisabled={disabled}
          selectedOptionIndex={this.state.selectedOptionIndex}
          options={this.state.options}
          value={this.state.value}
          onChange={this.onChange}
          isMulti={false}
          isRTL={isRTL}
          placeholder={placeholder}
          loadingMessage={loadingMessage}
          noOptionsMessage={noOptionsMessage}
        />
      </div>
    );
  }
}
